import { Component, ViewChild, ViewEncapsulation } from "@angular/core";
import { TaskRoutes } from "@task-utils/routes";
import { Task } from "@task-utils/types";
import { DataTableComponent, Frame, GenericFilter, GenericFilterComponent, language, template, date } from "curvy";
import { jobs_management_translations } from "./jobs.trans";
import { TaskFilterComponent } from "@task-components/single-row-filter/single-row-filter.component";

type TempStaffPlan = TaskRoutes.temp_staff_requests.TempStaffPlan;

@Component({
   selector: 'task-working-hours',
   template: `
      <knw-data-table #workingHoursTable [config]="tableConfig"></knw-data-table>
   `,
   encapsulation: ViewEncapsulation.None,
   styles: [`
      task-working-hours {
         display: flex;
         flex-flow: column nowrap;
         overflow: hidden !important;
      }
   `],
})

export class WorkingHoursComponent {
   @ViewChild('workingHoursTable') table: DataTableComponent<TempStaffPlan, DisplayWorkingHours, 'working_hours', TaskFilterComponent>;

   constructor() {
      Frame.set({
         title: "NAVROUTE.WORKING_HOURS",
         visible: true,
         layout: "middle",
         size: "full"
      });

      language.load_translations(jobs_management_translations);
   }

   get_filters = async () => {
      let filters: TaskFilterComponent["filterInput"] = [
         GenericFilter.text("TITLE", "request.title"),
         GenericFilter.text("NAME", "application.first_name+ +application.last_name"),
         GenericFilter.search("LOCATION", "location", this.search_locations, this.format_location_name),
         GenericFilter.date_filter("FROM", "planned_start_from"),
         GenericFilter.date_filter("TO", "planned_end_to"),
      ];

      return filters;
   }

   async search_locations(search: string) {
      let res = await TaskRoutes.locations.api_location_search_visible(
         { search: [search], attributes: ["cin+ +name"] },
         { page_no: 1, page_size: 50 }
      );
      return res.data;
   }

   format_location_name(loc: Task.Location) {
      if (!loc) { return ""; }
      return `${loc.cin ?? ''} ${loc.name}`;
   }

   tableConfig: DataTableComponent<TempStaffPlan, DisplayWorkingHours, 'working_hours', GenericFilterComponent>["tableConfig"] = {
      tableName: 'working_hours',
      getData: async (filter, paginator, sort_header) => {
         let parsed_filter = { search: [], attributes: [], sort: null };
         for (let key in filter) {
            if (key == 'location') {
               parsed_filter.attributes.push('#=:request.location_id');
               parsed_filter.search.push(filter[key].location_id);

            } else if (key == 'planned_start_from') {
               let start = date.start_of_day(filter[key]);
               parsed_filter.attributes.push('-d:planned_start_time');
               parsed_filter.search.push(start.toISOString());

            } else if (key == 'planned_end_to') {
               let end = date.end_of_day(filter[key]);
               parsed_filter.attributes.push('+d:planned_end_time');
               parsed_filter.search.push(end.toISOString());

            } else {
               parsed_filter.attributes.push(key);
               parsed_filter.search.push(filter[key]);
            }
         }

         if (sort_header) {
            parsed_filter.sort = sort_header;
         } else {
            parsed_filter.sort = [{
               attribute: "#:temp_staff_plan_id",
               label: "#:temp_staff_plan_id",
               direction: "desc",
               ascending: false
            }];
         }

         return await TaskRoutes.temp_staff_requests.api_temp_staff_plans_search(parsed_filter, paginator);
      },
      getHeader: (header) => {
         switch (header) {
            case 'name':
               return { icon: "person", label: "NAME", sortable: false };
            case 'title':
               return { icon: "title", label: "TITLE", sortable: false };
            case 'date':
               return { icon: "event", label: "DATE", sortable: true };
            case 'planned_start_time':
               return { icon: "schedule", label: "PLANNED_START", sortable: true };
            case 'planned_end_time':
               return { icon: "schedule", label: "PLANNED_END", sortable: true };
            case 'start_time':
               return { icon: "play_arrow", label: "ACTUAL_START", sortable: true };
            case 'end_time':
               return { icon: "stop", label: "ACTUAL_END", sortable: true };
         }
      },

      unwrapData: (plan) => {
         function datetime_template(datetime: Date|string|null, date_format: string = "short_date_time") {
            if (datetime) {
               return template.transform(`
                  <div style="white-space: nowrap">
                     {{ datetime | date << ${date_format} }}
                  </div>
                  `,
                  { datetime }
               );
            } else {
               return null;
            }
         }

         return {
            name: template.transform(`
               <div style="white-space: nowrap">{{ first_name }} {{ last_name }}</div>
            `, { first_name: plan.first_name, last_name: plan.last_name }),
            title: template.transform(`
               <span class="truncate-text">{{ title }}</span>
            `, { title: plan.title }),
            date: datetime_template(plan.planned_start_time ?? plan.start_time, 'short_date'),
            planned_start_time: datetime_template(plan.planned_start_time, 'time'),
            planned_end_time: datetime_template(plan.planned_end_time, 'time'),
            start_time: datetime_template(plan.start_time, 'time'),
            end_time: datetime_template(plan.end_time, 'time'),
         }
      },
      maxActions: 0,
      filterComponent: GenericFilterComponent,
      filterInput: this.get_filters,
      rowActions: []
   }

}

type DisplayWorkingHours = {
   name: template.EscapedHTML;
   date: template.EscapedHTML;
   title: template.EscapedHTML;
   planned_start_time: template.EscapedHTML;
   planned_end_time: template.EscapedHTML;
   start_time: template.EscapedHTML;
   end_time: template.EscapedHTML;
}
